import { useState, useEffect } from 'react';
import { FileText, TrendingUp, AlertTriangle, CheckCircle, Download, Eye, CreditCard as Edit2, Save, Loader, Calendar, Star, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Sparkline } from './Sparkline';

interface TopItem {
  label: string;
  amount: number;
  percentage: number;
}

interface SparklineData {
  encaissements: (number | null)[];
  decaissements: (number | null)[];
  resultat: (number | null)[];
  tresorerie: (number | null)[];
}

interface FinancialReport {
  id: string;
  periode_mois: string;
  statut_global: 'vert' | 'orange' | 'rouge';
  tresorerie_fin_mois: number;
  variation_tresorerie: number;
  resultat_mensuel_estime: number;
  charges_totales: number;
  ecart_notable_titre: string;
  ecart_notable_description: string;
  impact: 'faible' | 'modere' | 'significatif';
  action_recommandee: string;
  texte_lecture_guidee: string;
  note_synthese: string | null;
  note_synthese_statut: 'a_faire' | 'en_cours' | 'terminee';
  genere_le: string;
  genere_par: string;
  encaissements_mois?: number;
  decaissements_mois?: number;
  encaissements_mois_precedent?: number;
  decaissements_mois_precedent?: number;
  tresorerie_mois_precedent?: number;
  sparkline_data?: SparklineData;
  score_pilotage?: number;
  score_pilotage_legende?: string;
  top_recettes?: TopItem[];
  top_charges?: TopItem[];
  has_new_indicators?: boolean;
}

interface FinancialReportsSectionProps {
  userId: string;
  isEligible: boolean;
}

export function FinancialReportsSection({ userId, isEligible }: FinancialReportsSectionProps) {
  console.log('[FinancialReportsSection] RENDER - userId:', userId, 'isEligible:', isEligible);

  const [reports, setReports] = useState<FinancialReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState<FinancialReport | null>(null);
  const [editingNote, setEditingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth()); // 0-11

  useEffect(() => {
    console.log('[FinancialReportsSection] useEffect - isEligible:', isEligible, 'userId:', userId);
    if (isEligible) {
      fetchReports();
    }
  }, [userId, isEligible]);

  async function fetchReports() {
    console.log('[FinancialReportsSection] fetchReports called for userId:', userId);
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('rapports_financiers')
        .select('*')
        .eq('user_id', userId)
        .order('periode_mois', { ascending: false });

      console.log('[FinancialReportsSection] fetchReports result:', { data, error, count: data?.length || 0 });
      if (error) throw error;
      setReports(data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  }

  async function generateReport() {
    const periodeMois = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;

    if (!confirm(`Générer le rapport pour ${periodeMois} ?`)) return;

    setGenerating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-financial-report`;

      console.log('[FinancialReportsSection] Generating report for:', { userId, periodeMois });
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId, periode_mois: periodeMois }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la génération');
      }

      console.log('[FinancialReportsSection] Report generated successfully, refreshing...');
      alert('Rapport généré avec succès');
      await fetchReports();
    } catch (error) {
      console.error('Error generating report:', error);
      alert(error instanceof Error ? error.message : 'Erreur lors de la génération du rapport');
    } finally {
      setGenerating(false);
    }
  }

  function viewReport(report: FinancialReport) {
    setSelectedReport(report);
    setNoteText(report.note_synthese || '');
    setEditingNote(false);
  }

  async function saveNote() {
    if (!selectedReport) return;

    setSavingNote(true);
    try {
      const { error } = await supabase
        .from('rapports_financiers')
        .update({ note_synthese: noteText })
        .eq('id', selectedReport.id);

      if (error) throw error;

      setSelectedReport({ ...selectedReport, note_synthese: noteText });
      setEditingNote(false);
      await fetchReports();
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Erreur lors de l\'enregistrement de la note');
    } finally {
      setSavingNote(false);
    }
  }

  async function markNoteComplete() {
    if (!selectedReport) return;

    try {
      const { error: reportError } = await supabase
        .from('rapports_financiers')
        .update({ note_synthese_statut: 'terminee' })
        .eq('id', selectedReport.id);

      if (reportError) throw reportError;

      const { error: alertError } = await supabase
        .from('alertes')
        .update({ statut: 'traitee' })
        .eq('rapport_id', selectedReport.id)
        .eq('type', 'note_synthese_a_rediger');

      if (alertError) throw alertError;

      setSelectedReport({ ...selectedReport, note_synthese_statut: 'terminee' });
      await fetchReports();
      alert('Note marquée comme terminée');
    } catch (error) {
      console.error('Error marking note complete:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  }

  async function exportToPDF(report: FinancialReport) {
    try {
      const { data: userData } = await supabase
        .from('user_profiles')
        .select('first_name, last_name, company')
        .eq('id', userId)
        .maybeSingle();

      const clientName = userData?.company ||
        `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() ||
        'Client';

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Rapport Financier Mensuel', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Période: ${report.periode_mois}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc.text(`Client: ${clientName}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      const statusColors = {
        vert: [34, 197, 94],
        orange: [249, 115, 22],
        rouge: [239, 68, 68]
      };
      const statusLabels = {
        vert: 'Situation saine',
        orange: 'Attention requise',
        rouge: 'Action urgente'
      };

      doc.setFillColor(...statusColors[report.statut_global]);
      doc.rect(15, yPos, pageWidth - 30, 12, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text(`Statut: ${statusLabels[report.statut_global]}`, pageWidth / 2, yPos + 8, { align: 'center' });
      yPos += 20;

      doc.setTextColor(0, 0, 0);

      if (report.score_pilotage !== undefined && report.score_pilotage_legende) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('Score de pilotage', 15, yPos);
        yPos += 8;
        doc.setFontSize(24);
        const scoreColor = report.score_pilotage >= 80 ? [34, 197, 94] : report.score_pilotage >= 60 ? [234, 179, 8] : [239, 68, 68];
        doc.setTextColor(...scoreColor);
        doc.text(`${report.score_pilotage}/100`, 15, yPos);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
        doc.text(`(${report.score_pilotage_legende})`, 50, yPos);
        yPos += 15;
      }

      if (report.has_new_indicators && report.encaissements_mois !== undefined) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text('Indicateurs clés (comparatif)', 15, yPos);
        yPos += 10;

        const kpiData = [];
        kpiData.push([
          'Encaissements M',
          `${report.encaissements_mois.toFixed(2)} €`,
          report.encaissements_mois_precedent !== undefined ? `${report.encaissements_mois_precedent.toFixed(2)} €` : 'N/A',
          report.encaissements_mois_precedent !== undefined
            ? `${((report.encaissements_mois - report.encaissements_mois_precedent) / Math.abs(report.encaissements_mois_precedent) * 100).toFixed(1)}%`
            : 'N/A'
        ]);
        kpiData.push([
          'Décaissements M',
          `${report.decaissements_mois.toFixed(2)} €`,
          report.decaissements_mois_precedent !== undefined ? `${report.decaissements_mois_precedent.toFixed(2)} €` : 'N/A',
          report.decaissements_mois_precedent !== undefined
            ? `${((report.decaissements_mois - report.decaissements_mois_precedent) / Math.abs(report.decaissements_mois_precedent) * 100).toFixed(1)}%`
            : 'N/A'
        ]);
        kpiData.push([
          'Résultat M',
          `${report.resultat_mensuel_estime.toFixed(2)} €`,
          '-',
          '-'
        ]);
        if (report.tresorerie_mois_precedent !== undefined) {
          kpiData.push([
            'Trésorerie fin M',
            `${report.tresorerie_fin_mois.toFixed(2)} €`,
            `${report.tresorerie_mois_precedent.toFixed(2)} €`,
            `${((report.tresorerie_fin_mois - report.tresorerie_mois_precedent) / Math.abs(report.tresorerie_mois_precedent) * 100).toFixed(1)}%`
          ]);
        }

        if (report.sparkline_data) {
          const sparklineText = `Tendance (M-3, M-2, M-1, M): ${report.sparkline_data.encaissements.map(v => v !== null ? v.toFixed(0) : 'N/A').join(', ')}`;
          kpiData.push(['Sparkline Encaissements', sparklineText, '-', '-']);
        }

        autoTable(doc, {
          startY: yPos,
          head: [['Indicateur', 'M', 'M-1', 'Variation']],
          body: kpiData,
          theme: 'grid',
          headStyles: { fillColor: [59, 130, 246] },
          margin: { left: 15, right: 15 }
        });

        yPos = (doc as any).lastAutoTable.finalY + 15;

        if (yPos > doc.internal.pageSize.getHeight() - 60) {
          doc.addPage();
          yPos = 20;
        }

        if (report.top_recettes && report.top_recettes.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.text('Top 3 recettes du mois', 15, yPos);
          yPos += 10;

          const topRecettesData = report.top_recettes.map(item => [
            item.label,
            `${item.amount.toFixed(2)} €`,
            `${item.percentage.toFixed(1)}%`
          ]);

          autoTable(doc, {
            startY: yPos,
            head: [['Libellé', 'Montant', '% du total']],
            body: topRecettesData,
            theme: 'grid',
            headStyles: { fillColor: [34, 197, 94] },
            margin: { left: 15, right: 15 }
          });

          yPos = (doc as any).lastAutoTable.finalY + 15;
        }

        if (yPos > doc.internal.pageSize.getHeight() - 60) {
          doc.addPage();
          yPos = 20;
        }

        if (report.top_charges && report.top_charges.length > 0) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.text('Top 3 charges du mois', 15, yPos);
          yPos += 10;

          const topChargesData = report.top_charges.map(item => [
            item.label,
            `${item.amount.toFixed(2)} €`,
            `${item.percentage.toFixed(1)}%`
          ]);

          autoTable(doc, {
            startY: yPos,
            head: [['Libellé', 'Montant', '% du total']],
            body: topChargesData,
            theme: 'grid',
            headStyles: { fillColor: [239, 68, 68] },
            margin: { left: 15, right: 15 }
          });

          yPos = (doc as any).lastAutoTable.finalY + 15;
        }

        if (yPos > doc.internal.pageSize.getHeight() - 60) {
          doc.addPage();
          yPos = 20;
        }
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('Indicateurs clés', 15, yPos);
      yPos += 10;

      const indicators = [
        ['Trésorerie fin de mois', `${report.tresorerie_fin_mois.toFixed(2)} €`],
        ['Variation trésorerie', `${report.variation_tresorerie.toFixed(2)} €`],
        ['Résultat mensuel estimé', `${report.resultat_mensuel_estime.toFixed(2)} €`],
        ['Charges totales', `${report.charges_totales.toFixed(2)} €`]
      ];

      autoTable(doc, {
        startY: yPos,
        head: [['Indicateur', 'Valeur']],
        body: indicators,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 15, right: 15 }
      });

      yPos = (doc as any).lastAutoTable.finalY + 15;

      doc.setFillColor(254, 243, 199);
      doc.rect(15, yPos, pageWidth - 30, 30, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Écart notable', 18, yPos + 8);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const ecartLines = doc.splitTextToSize(report.ecart_notable_description, pageWidth - 40);
      doc.text(ecartLines, 18, yPos + 15);
      yPos += 40;

      doc.setFillColor(219, 234, 254);
      doc.rect(15, yPos, pageWidth - 30, 35, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Lecture guidée', 18, yPos + 8);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const lectureLines = doc.splitTextToSize(report.texte_lecture_guidee, pageWidth - 40);
      doc.text(lectureLines, 18, yPos + 15);
      yPos += 45;

      if (yPos > doc.internal.pageSize.getHeight() - 50) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFillColor(243, 232, 255);
      doc.rect(15, yPos, pageWidth - 30, 25, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Action recommandée', 18, yPos + 8);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const actionLines = doc.splitTextToSize(report.action_recommandee, pageWidth - 40);
      doc.text(actionLines, 18, yPos + 15);
      yPos += 35;

      if (report.note_synthese) {
        if (yPos > doc.internal.pageSize.getHeight() - 60) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFillColor(243, 244, 246);
        doc.rect(15, yPos, pageWidth - 30, 40, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('Note de synthèse', 18, yPos + 8);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        const noteLines = doc.splitTextToSize(report.note_synthese, pageWidth - 40);
        doc.text(noteLines, 18, yPos + 15);
      }

      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Généré le ${new Date().toLocaleDateString('fr-FR')} - ${clientName}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );

      doc.save(`rapport-financier-${report.periode_mois}-${clientName}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Erreur lors de l\'export PDF');
    }
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      vert: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Sain' },
      orange: { bg: 'bg-orange-100', text: 'text-orange-800', icon: AlertTriangle, label: 'Attention' },
      rouge: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertTriangle, label: 'Urgent' }
    };
    const badge = badges[status as keyof typeof badges];
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        <Icon size={16} />
        {badge.label}
      </span>
    );
  };

  const getNoteStatusBadge = (status: string) => {
    const badges = {
      a_faire: { bg: 'bg-red-100', text: 'text-red-800', label: 'À faire' },
      en_cours: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En cours' },
      terminee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminée' }
    };
    const badge = badges[status as keyof typeof badges];
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getScoreBadge = (score: number, legende: string) => {
    const colors = score >= 80
      ? { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' }
      : score >= 60
      ? { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' }
      : { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' };

    return (
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 ${colors.bg} ${colors.text} ${colors.border}`}>
        <Star className="fill-current" size={20} />
        <div className="text-left">
          <div className="text-2xl font-bold">{score}/100</div>
          <div className="text-xs">{legende}</div>
        </div>
      </div>
    );
  };

  const getVariationIndicator = (current: number, previous: number) => {
    const variation = current - previous;
    const variationPercent = previous !== 0 ? (variation / Math.abs(previous)) * 100 : 0;

    if (Math.abs(variation) < 0.01) {
      return (
        <div className="flex items-center gap-1 text-xs text-gray-600">
          <Minus size={14} />
          <span>Stable</span>
        </div>
      );
    }

    const isPositive = variation > 0;
    return (
      <div className={`flex items-center gap-1 text-xs ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
        <span>{isPositive ? '+' : ''}{variation.toFixed(2)} € ({isPositive ? '+' : ''}{variationPercent.toFixed(1)}%)</span>
      </div>
    );
  };

  return (
    <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <TrendingUp className="text-blue-600" size={20} />
        Rapports Financiers (Offre 149€/mois)
      </h3>

      {!isEligible ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-semibold text-gray-900 mb-1">Utilisateur non éligible</p>
              <p className="text-sm text-gray-700">
                Cet utilisateur n'a pas souscrit au Pack Pilotage (69€/mois) ou supérieur.
                Pour générer des rapports financiers automatisés, l'utilisateur doit avoir un abonnement actif avec le Pack Pilotage ou DAF.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Calendar size={16} />
              Sélectionner la période
            </label>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">Mois</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={0}>Janvier</option>
                  <option value={1}>Février</option>
                  <option value={2}>Mars</option>
                  <option value={3}>Avril</option>
                  <option value={4}>Mai</option>
                  <option value={5}>Juin</option>
                  <option value={6}>Juillet</option>
                  <option value={7}>Août</option>
                  <option value={8}>Septembre</option>
                  <option value={9}>Octobre</option>
                  <option value={10}>Novembre</option>
                  <option value={11}>Décembre</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">Année</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <button
            onClick={generateReport}
            disabled={generating || loading}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
          >
            {generating ? (
              <>
                <Loader className="animate-spin" size={20} />
                Génération en cours...
              </>
            ) : (
              <>
                <FileText size={20} />
                Générer le rapport pour {String(selectedMonth + 1).padStart(2, '0')}/{selectedYear}
              </>
            )}
          </button>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : reports.length > 0 ? (
          <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
            <div className="px-4 py-3 bg-blue-100 border-b border-blue-200">
              <h4 className="font-semibold text-gray-900">Rapports générés</h4>
            </div>
            <div className="divide-y divide-gray-200">
              {reports.map((report) => (
                <div key={report.id} className="px-4 py-3 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-gray-900">{report.periode_mois}</span>
                      {getStatusBadge(report.statut_global)}
                      {report.has_new_indicators && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          Nouveaux indicateurs
                        </span>
                      )}
                      {report.note_synthese_statut !== 'terminee' && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                          Note à rédiger
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => viewReport(report)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                      >
                        <Eye size={14} />
                        Voir
                      </button>
                      <button
                        onClick={() => exportToPDF(report)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      >
                        <Download size={14} />
                        PDF
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Généré le {new Date(report.genere_le).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-blue-200 p-6 text-center text-gray-500">
            Aucun rapport généré
          </div>
        )}

        {selectedReport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Rapport {selectedReport.periode_mois}</h3>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusBadge(selectedReport.statut_global)}
                    {selectedReport.score_pilotage !== undefined && selectedReport.score_pilotage_legende && (
                      getScoreBadge(selectedReport.score_pilotage, selectedReport.score_pilotage_legende)
                    )}
                    <span className="text-sm text-gray-500">
                      Impact: <span className="font-medium capitalize">{selectedReport.impact}</span>
                    </span>
                  </div>
                </div>

                {selectedReport.has_new_indicators && selectedReport.encaissements_mois !== undefined && (
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-blue-200">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingUp size={20} className="text-blue-600" />
                      Indicateurs clés (comparatif)
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <div className="text-sm text-gray-600 mb-2">Encaissements du mois</div>
                        <div className="text-2xl font-bold text-green-600 mb-2">
                          {selectedReport.encaissements_mois.toFixed(2)} €
                        </div>
                        {selectedReport.encaissements_mois_precedent !== undefined && (
                          <>
                            <div className="text-xs text-gray-500 mb-1">
                              M-1: {selectedReport.encaissements_mois_precedent.toFixed(2)} €
                            </div>
                            {getVariationIndicator(selectedReport.encaissements_mois, selectedReport.encaissements_mois_precedent)}
                          </>
                        )}
                        {selectedReport.sparkline_data && (
                          <div className="mt-3">
                            <Sparkline data={selectedReport.sparkline_data.encaissements} color="#10b981" />
                          </div>
                        )}
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <div className="text-sm text-gray-600 mb-2">Décaissements du mois</div>
                        <div className="text-2xl font-bold text-red-600 mb-2">
                          {selectedReport.decaissements_mois.toFixed(2)} €
                        </div>
                        {selectedReport.decaissements_mois_precedent !== undefined && (
                          <>
                            <div className="text-xs text-gray-500 mb-1">
                              M-1: {selectedReport.decaissements_mois_precedent.toFixed(2)} €
                            </div>
                            {getVariationIndicator(selectedReport.decaissements_mois, selectedReport.decaissements_mois_precedent)}
                          </>
                        )}
                        {selectedReport.sparkline_data && (
                          <div className="mt-3">
                            <Sparkline data={selectedReport.sparkline_data.decaissements} color="#ef4444" />
                          </div>
                        )}
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <div className="text-sm text-gray-600 mb-2">Résultat du mois</div>
                        <div className={`text-2xl font-bold mb-2 ${selectedReport.resultat_mensuel_estime >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {selectedReport.resultat_mensuel_estime.toFixed(2)} €
                        </div>
                        {selectedReport.sparkline_data && (
                          <div className="mt-3">
                            <Sparkline
                              data={selectedReport.sparkline_data.resultat}
                              color={selectedReport.resultat_mensuel_estime >= 0 ? '#10b981' : '#ef4444'}
                            />
                          </div>
                        )}
                      </div>

                      {selectedReport.tresorerie_mois_precedent !== undefined && (
                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                          <div className="text-sm text-gray-600 mb-2">Trésorerie fin de mois</div>
                          <div className="text-2xl font-bold text-blue-600 mb-2">
                            {selectedReport.tresorerie_fin_mois.toFixed(2)} €
                          </div>
                          <div className="text-xs text-gray-500 mb-1">
                            M-1: {selectedReport.tresorerie_mois_precedent.toFixed(2)} €
                          </div>
                          {getVariationIndicator(selectedReport.tresorerie_fin_mois, selectedReport.tresorerie_mois_precedent)}
                          {selectedReport.sparkline_data && (
                            <div className="mt-3">
                              <Sparkline data={selectedReport.sparkline_data.tresorerie} color="#3b82f6" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedReport.top_recettes && selectedReport.top_recettes.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ArrowUp className="text-green-600" size={18} />
                      Top 3 recettes du mois
                    </h4>
                    {selectedReport.top_recettes.length > 0 ? (
                      <div className="space-y-2">
                        {selectedReport.top_recettes.map((item, index) => (
                          <div key={index} className="bg-white rounded p-3 flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 text-sm">{item.label}</div>
                              <div className="text-xs text-gray-500">{item.percentage.toFixed(1)}% du total</div>
                            </div>
                            <div className="text-lg font-bold text-green-600">
                              {item.amount.toFixed(2)} €
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">Aucune recette enregistrée ce mois-ci.</p>
                    )}
                  </div>
                )}

                {selectedReport.top_charges && selectedReport.top_charges.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <ArrowDown className="text-red-600" size={18} />
                      Top 3 charges du mois
                    </h4>
                    {selectedReport.top_charges.length > 0 ? (
                      <div className="space-y-2">
                        {selectedReport.top_charges.map((item, index) => (
                          <div key={index} className="bg-white rounded p-3 flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 text-sm">{item.label}</div>
                              <div className="text-xs text-gray-500">{item.percentage.toFixed(1)}% du total</div>
                            </div>
                            <div className="text-lg font-bold text-red-600">
                              {item.amount.toFixed(2)} €
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">Aucune charge enregistrée ce mois-ci.</p>
                    )}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="text-sm text-gray-600 mb-1">Trésorerie fin de mois</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {selectedReport.tresorerie_fin_mois.toFixed(2)} €
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <div className="text-sm text-gray-600 mb-1">Variation trésorerie</div>
                    <div className={`text-2xl font-bold ${
                      selectedReport.variation_tresorerie >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {selectedReport.variation_tresorerie >= 0 ? '+' : ''}{selectedReport.variation_tresorerie.toFixed(2)} €
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="text-sm text-gray-600 mb-1">Résultat mensuel estimé</div>
                    <div className={`text-2xl font-bold ${
                      selectedReport.resultat_mensuel_estime >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {selectedReport.resultat_mensuel_estime.toFixed(2)} €
                    </div>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <div className="text-sm text-gray-600 mb-1">Charges totales</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {selectedReport.charges_totales.toFixed(2)} €
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <div className="font-semibold text-gray-900 mb-2">Écart notable</div>
                  <p className="text-gray-700">{selectedReport.ecart_notable_description}</p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="font-semibold text-gray-900 mb-2">Lecture guidée</div>
                  <p className="text-gray-700">{selectedReport.texte_lecture_guidee}</p>
                </div>

                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <div className="font-semibold text-gray-900 mb-2">Action recommandée</div>
                  <p className="text-gray-700">{selectedReport.action_recommandee}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-gray-900">Note de synthèse</div>
                      {getNoteStatusBadge(selectedReport.note_synthese_statut)}
                    </div>
                    {!editingNote && (
                      <button
                        onClick={() => setEditingNote(true)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        <Edit2 size={14} />
                        Éditer
                      </button>
                    )}
                  </div>

                  {editingNote ? (
                    <div className="space-y-2">
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
                        placeholder="Rédigez votre note de synthèse..."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={saveNote}
                          disabled={savingNote}
                          className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          {savingNote ? (
                            <>
                              <Loader className="animate-spin" size={16} />
                              Enregistrement...
                            </>
                          ) : (
                            <>
                              <Save size={16} />
                              Enregistrer
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setEditingNote(false);
                            setNoteText(selectedReport.note_synthese || '');
                          }}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {selectedReport.note_synthese || 'Aucune note rédigée'}
                    </p>
                  )}

                  {selectedReport.note_synthese && selectedReport.note_synthese_statut !== 'terminee' && (
                    <button
                      onClick={markNoteComplete}
                      className="mt-3 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Marquer comme terminée
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      )}
    </div>
  );
}
